import React from 'react';

// Presentational data marks for the Settings console.
// Meter: part-to-whole stacked bar. RankBars: categorical comparison.
// Every mark is role="img" with an aria-label. Colour is never the only channel.

interface Segment {
  key: string;
  name: string;
  value: number;
  tone: string;
}

function drawable(segments: Segment[]): Segment[] {
  return segments.filter((s) => Number(s.value) > 0);
}

function describe(label: string, segments: Segment[], total?: number): string {
  const parts = segments.map((s) => `${s.name} ${s.value}`);
  const of = total != null ? ` of ${total}` : '';
  return `${label}${of}: ${parts.join(', ')}.`;
}

interface MeterProps {
  segments: Segment[];
  label: string;
  total?: number;
  discrete?: boolean;
  description?: string;
}

export function Meter({ segments, label, total, discrete = false, description }: MeterProps) {
  const list = drawable(segments);
  const sum = list.reduce((n, s) => n + Number(s.value), 0);
  if (!sum) return null;
  return (
    <div
      className={`stg-meter${discrete ? ' is-discrete' : ''}`}
      role="img"
      aria-label={description || describe(label, segments, total)}
    >
      {list.map((s, i) => (
        <span
          key={s.key}
          className={`stg-meter-seg t-${s.tone}`}
          title={`${s.name} · ${s.value}`}
          style={{
            flexGrow: discrete ? undefined : Number(s.value),
            flexBasis: discrete ? undefined : 0,
            animationDelay: `${Math.min(i, 5) * 30}ms`,
          }}
        />
      ))}
    </div>
  );
}

interface LegendItem {
  key: string;
  name: string;
  value: number;
  tone: string;
}

interface LegendProps {
  items: LegendItem[];
}

export function Legend({ items }: LegendProps) {
  return (
    <div className="stg-legend" aria-hidden="true">
      {items.map((s) => (
        <span className="stg-legend-i" key={s.key}>
          <span className={`stg-legend-d t-${s.tone}`} />
          {s.name}
          <span className={`stg-legend-v${Number(s.value) ? '' : ' is-zero'}`}>{s.value}</span>
        </span>
      ))}
    </div>
  );
}

interface RankRow {
  key: string;
  name: string;
  value: number;
  tone: string;
}

interface RankBarsProps {
  rows: RankRow[];
  label?: string;
  max?: number;
}

export function RankBars({ rows, label, max }: RankBarsProps) {
  const top = max != null ? max : rows.reduce((n, r) => Math.max(n, Number(r.value) || 0), 0);
  return (
    <div className="stg-bars" role="img" aria-label={label ? describe(label, rows) : ''}>
      {rows.map((r, i) => (
        <React.Fragment key={r.key}>
          <span className="stg-bar-l">{r.name}</span>
          <span className="stg-bar-t">
            {Number(r.value) > 0 ? (
              <span
                className={`stg-bar-f t-${r.tone}`}
                style={{
                  width: `${top ? (Number(r.value) / top) * 100 : 0}%`,
                  animationDelay: `${Math.min(i, 5) * 30}ms`,
                }}
              />
            ) : null}
          </span>
          <span className={`stg-bar-v${Number(r.value) ? '' : ' is-zero'}`}>{r.value}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

interface MicroBarProps {
  value: number;
  max: number;
}

export function MicroBar({ value, max }: MicroBarProps) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (Number(value) / max) * 100)) : 0;
  return (
    <span className="stg-micro-t" aria-hidden="true">
      <span className="stg-micro-f" style={{ width: `${pct}%` }} />
    </span>
  );
}

interface SummaryBandProps {
  eyebrow: string;
  value: string;
  children?: React.ReactNode;
  inset?: boolean;
}

export function SummaryBand({ eyebrow, value, children, inset = false }: SummaryBandProps) {
  return (
    <div className={`stg-summary${inset ? ' is-inset' : ''}`}>
      <div className="stg-summary-t">
        <span className="stg-eyebrow">{eyebrow}</span>
        <span className="stg-summary-v">{value}</span>
      </div>
      {children ? <div className="stg-summary-g">{children}</div> : null}
    </div>
  );
}

export default Meter;
